import {
    ContainerFilled,
    ExclamationCircleFilled,
    FileDoneOutlined,
    LeftOutlined,
    MenuOutlined,
    SyncOutlined,
    UnorderedListOutlined,
    UsergroupAddOutlined,
    UserOutlined,
    CheckSquareFilled,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Divider, Menu } from 'antd';
import Sider from 'antd/lib/layout/Sider';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import CustomContext from '../../context/CustomContext';
import { PageType, providerAuthTypes } from '../../model/types';
import rolesService from '../../services/RolesService';
import styles from './MenuComponent.module.scss';

type MenuItem = Required<MenuProps>['items'][number];

const MenuComponent = (props: Props): React.ReactElement => {
    const { pageId } = props;

    /*** HOOKS ***/

    const context = useContext(CustomContext);
    const { auth, providerAuths } = context;
    const [menuCollapsed, setMenuCollapsed] = useState(
        localStorage.getItem('menuCollapsed') !== null && localStorage.getItem('menuCollapsed') === 'true',
    );

    let selectedKeys = pageId ? [pageId] : [];
    const isMembersVisible = auth && auth.members.some((m) => m.bim360 === 'ADMIN');

    /** METHODS */

    const handleCollapseMenu = () => {
        const updatedMenuColapsed = !menuCollapsed;
        localStorage.setItem('menuCollapsed', updatedMenuColapsed.toString());
        setMenuCollapsed(updatedMenuColapsed);
    };

    /** VISUAL */

    const linksMenuItems: MenuItem[] = [
        {
            key: 'connections',
            label: (
                <Link to="/sync/connections">
                    <FormattedMessage id="home.connections.title" />
                </Link>
            ),
            icon: <SyncOutlined />,
        },
        ...(providerAuths.includes('AUTODESK')
            ? [
                  {
                      key: 'connectionPermissions',
                      label: (
                          <Link to="/sync/permissions">
                              <FormattedMessage id="connectionPermissions.title" />
                          </Link>
                      ),
                      icon: <CheckSquareFilled />,
                  },
              ]
            : []),
        {
            key: 'files',
            label: (
                <Link to="/sync/files">
                    <FormattedMessage id="home.filesAttributes.title" />
                </Link>
            ),
            icon: <UnorderedListOutlined />,
        },
    ];

    const setupMenuItems: MenuItem[] = [
        {
            key: 'projectMembers',
            label: (
                <Link to="/setup/project-members">
                    <FormattedMessage id="menu.users" />
                </Link>
            ),
            icon: <UserOutlined />,
        },
        ...(rolesService.hasAnyRole(auth, ['ROLE_ADMIN', 'ROLE_USER'])
            ? [
                  {
                      key: 'projectIssues',
                      label: (
                          <Link to="/setup/project-issues">
                              <FormattedMessage id="menu.issues" />
                          </Link>
                      ),
                      icon: <ExclamationCircleFilled />,
                  },
              ]
            : []),
        ...(rolesService.hasAnyRole(auth, ['ROLE_ADMIN'])
            ? [
                  {
                      key: 'projects',
                      label: (
                          <Link to="/setup/projects">
                              <FormattedMessage id="menu.projects" />
                          </Link>
                      ),
                      icon: <ContainerFilled />,
                  },
              ]
            : []),
        ...(isMembersVisible
            ? [
                  {
                      key: 'members',
                      label: (
                          <Link to="/setup/members">
                              <FormattedMessage id="menu.directory" />
                          </Link>
                      ),
                      icon: <UsergroupAddOutlined />,
                  },
              ]
            : []),
    ];

    const taskMenuItems: MenuItem[] = [
        {
            key: 'tasks',
            label: (
                <Link to="/tasks">
                    <FormattedMessage id="header.apps.tasks" />
                </Link>
            ),
            icon: <FileDoneOutlined />,
        },
    ];

    if (pageId && ['home', 'error', 'loginError', 'loading'].includes(pageId)) {
        return <></>;
    } else {
        return (
            <Sider
                collapsible
                collapsed={menuCollapsed}
                onCollapse={(value) => setMenuCollapsed(value)}
                className={styles.sider}
                theme="light"
                width="250"
                collapsedWidth="100"
                trigger={null}
            >
                {menuCollapsed ? (
                    <div className={styles.menuButton}>
                        <Button type="text" icon={<MenuOutlined />} size="small" onClick={handleCollapseMenu} />
                    </div>
                ) : (
                    <div className={styles.menuHeader}>
                        <div className={styles.title}>
                            <FormattedMessage id="menu.title" />
                        </div>
                        <Button type="text" icon={<LeftOutlined />} size="small" onClick={handleCollapseMenu} />
                    </div>
                )}
                {providerAuths.some((a) => providerAuthTypes.includes(a)) ? (
                    <>
                        {menuCollapsed ? (
                            <Divider className={styles.divider} />
                        ) : (
                            <div className={styles.menuSectionTitle}>
                                <FormattedMessage id="menu.links" />
                            </div>
                        )}
                        <Menu
                            mode="inline"
                            selectedKeys={selectedKeys}
                            items={linksMenuItems}
                            inlineCollapsed={menuCollapsed}
                        />
                    </>
                ) : (
                    <></>
                )}
                {providerAuths.includes('AUTODESK') ? (
                    <>
                        {menuCollapsed ? (
                            <Divider className={styles.divider} />
                        ) : (
                            <div className={styles.menuSectionTitle}>
                                <FormattedMessage id="menu.accSetup" />
                            </div>
                        )}
                        <Menu
                            mode="inline"
                            selectedKeys={selectedKeys}
                            items={setupMenuItems}
                            inlineCollapsed={menuCollapsed}
                        />
                    </>
                ) : (
                    <></>
                )}
                {menuCollapsed ? (
                    <Divider className={styles.divider} />
                ) : (
                    <div className={styles.menuSectionTitle}>
                        <FormattedMessage id="menu.taskManager" />
                    </div>
                )}
                <Menu mode="inline" selectedKeys={selectedKeys} items={taskMenuItems} inlineCollapsed={menuCollapsed} />
            </Sider>
        );
    }
};

export default MenuComponent;

interface Props {
    pageId?: PageType;
}
